<template>
	<div ref="observerTarget">
		<div ref="sliderRef" class="keen-slider">
			<div class="keen-slider__slide" v-for="(slide, index) in slides" :key="index">
				<slot :slide="slide" />
			</div>
		</div>
		<div v-if="showDots" class="agile__dots">
			<div
				v-for="(_, index) in slides"
				:key="index"
				class="agile__dot"
				:class="{ 'agile__dot--current': currentSlideIndex === index }"
			>
				<button @click="goToSlide(index)"></button>
			</div>
		</div>
	</div>
</template>

<script>
import 'keen-slider/keen-slider.min.css'
import KeenSlider from 'keen-slider'
import Jet, { typeLong } from '@/modules/Jet'

export default {
	name: 'Carousel',

	props: {
		slides: {
			type: Array,
			required: true,
		},

		analyticsContentType: {
			type: String,
			required: true,
		},

		analyticsPlacement: {
			type: String,
			required: true,
		},

		centerMode: {
			type: Boolean,
			default: false,
		},

		slidesToShow: {
			type: Number,
			default: 1,
		},

		infinite: {
			type: Boolean,
			default: true,
		},

		scaleEffect: {
			type: Boolean,
			default: false,
		},

		dragSpeed: {
			type: Number,
			default: 1,
		},

		// Auto-advance interval in ms, 0 = disabled
		autoplay: {
			type: Number,
			default: 0,
		},
	},

	emits: ['afterChange', 'beforeChange'],

	data: () => ({
		analyticsAlreadySentEvents: new Set(),
		slider: null,
		currentSlideIndex: 0,
		intersectionObserver: null,
	}),

	computed: {
		isCarouselDisabled() {
			return this.slides.length < 2
		},

		showDots() {
			return !this.isCarouselDisabled
		},
	},

	mounted() {
		if (!this.isCarouselDisabled) {
			this.initSlider()
		} else {
			this.$emit('afterChange', 0)
		}

		this.initIntersectionObserver()
	},

	beforeUnmount() {
		if (this.slider) {
			this.slider.destroy()
			this.slider = null
		}
		if (this.intersectionObserver) {
			this.intersectionObserver.disconnect()
			this.intersectionObserver = null
		}
	},

	methods: {
		initSlider() {
			this.slider = new KeenSlider(
				this.$refs.sliderRef,
				{
					loop: this.infinite,
					mode: 'snap',
					dragSpeed: this.dragSpeed,
					slides: {
						perView: this.slidesToShow,
						origin: this.centerMode ? 'center' : 'auto',
					},

					detailsChanged: (s) => {
						if (this.scaleEffect) {
							this.applyScaleEffect(s)
						}
					},

					slideChanged: (s) => {
						const prevSlide = this.currentSlideIndex
						this.currentSlideIndex = s.track.details.rel

						this.$emit('beforeChange', {
							currentSlide: prevSlide,
							nextSlide: this.currentSlideIndex,
						})

						this.$emit('afterChange', this.currentSlideIndex)

						if (this.currentSlideIndex > 0) {
							this.sendAnalyticsEvent()
						}
					},
				},
				this.autoplay > 0 ? [this.autoplayPlugin] : [],
			)
		},

		autoplayPlugin(slider) {
			let timeout

			const clearNextTimeout = () => clearTimeout(timeout)
			const nextTimeout = () => {
				clearTimeout(timeout)
				timeout = setTimeout(() => slider.next(), this.autoplay)
			}

			slider.on('created', nextTimeout)
			slider.on('dragStarted', clearNextTimeout)
			slider.on('animationEnded', nextTimeout)
			slider.on('updated', nextTimeout)
			slider.on('destroyed', clearNextTimeout)
		},

		initIntersectionObserver() {
			this.intersectionObserver = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						if (entry.isIntersecting) {
							this.sendAnalyticsEvent()
							this.intersectionObserver.disconnect()
						}
					})
				},
				{ threshold: 0.5 },
			)
			this.intersectionObserver.observe(this.$refs.observerTarget)
		},

		applyScaleEffect(s) {
			const details = s.track.details
			if (!details) return

			const slideSize = details.slides[0]?.size ?? 1 / this.slidesToShow
			const centerPos = (1 - slideSize) / 2

			s.slides.forEach((slideEl, idx) => {
				const slide = details.slides[idx]
				if (!slide) return

				const distFromCenter = Math.abs(slide.distance - centerPos)
				const t = Math.min(distFromCenter / slideSize, 1)

				const scale = 1 - 0.25 * t
				const opacity = 1 - 0.6 * t

				const inner = slideEl.children[0]
				if (inner) {
					inner.style.transform = `scale(${scale})`
					inner.style.opacity = `${opacity}`
				}
			})
		},

		goToSlide(index) {
			if (this.slider) {
				this.slider.moveToIdx(index)
			}
		},

		sendAnalyticsEvent() {
			const slideIndex = this.currentSlideIndex
			const slideNumber = slideIndex + 1

			if (this.analyticsAlreadySentEvents.has(slideNumber)) {
				return
			}

			this.$analytic.logEvent('CarouselStepReach', {
				placement: this.analyticsPlacement,
				content_type: this.analyticsContentType,
				content: this.slides[slideIndex]?.id,
				step: slideNumber,
			})
			Jet.logUxEvent('CarouselStepReach', {
				placement: this.analyticsPlacement,
				contentType: this.analyticsContentType,
				content: this.slides[slideIndex]?.id,
				step: typeLong(slideNumber),
			})

			this.analyticsAlreadySentEvents.add(slideNumber)
		},
	},
}
</script>
